// Copyright 2026 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later

import { apiFetch, TUNNEL_BASE_URL } from "@/services/apiClient";
import { useEffect, useEffectEvent, useState } from "react";
import { toast } from "@/lib/toast";
import { useActiveAsset } from "./useActiveAsset";
import { useActiveOrg } from "./useActiveOrg";
import { useActiveProject } from "./useActiveProject";
import { useLoader } from "./useLoader";
import useAccessToken from "./useAccessToken";
import {
  readSessionStorage,
  removeSessionStorage,
  writeSessionStorage,
} from "./useSessionStorage";

// limitations under the License.
export function useAutosetup(
  listenForChanges: boolean,
  devguardApiUrl: string,
  scanner:
    "full" | "sca" | "container-scanning" | "secret-scanning" | "iac" | "sast",
) {
  const { waitFor, isLoading, Loader } = useLoader();
  const { accessToken, onCreateAccessToken } = useAccessToken();
  const activeOrg = useActiveOrg();
  const activeProject = useActiveProject();
  const asset = useActiveAsset();

  const [progress, setProgress] = useState<{
    [key: string]: {
      status: "notStarted" | "pending" | "success";
      message: string;
      url?: string;
    };
  }>({
    projectHook: {
      status: "notStarted",
      message: "Project Webhook is created",
    },
    projectVariables: {
      status: "notStarted",
      message:
        "Project variables (DEVGUARD_TOKEN, DEVGUARD_ASSET_NAME) are created",
    },
    pipeline: {
      status: "notStarted",
      message:
        "The pipeline is created. A new branch was pushed to the repository",
    },
    mergeRequest: {
      url: undefined,
      status: "notStarted",
      message: "A merge request is created",
    },
    ...(asset?.externalEntityProviderId && {
      inviteDevguardBot: {
        status: "notStarted",
        message:
          "DevGuard Bot is invited to the project (only required for GitLab/ openCode)",
      },
    }),
  });

  const handleAutosetup = waitFor<boolean, void>(
    async (pendingAutosetup = false) => {
      // check if the asset is an external entity
      if (asset?.externalEntityProviderId && !pendingAutosetup) {
        // we need to redirect the user to authorize the "autosetup" oauth2 application
        writeSessionStorage("pending-autosetup", "true");
        // eslint-disable-next-line react-hooks/immutability
        window.location.href =
          window.location.origin +
          TUNNEL_BASE_URL +
          "/oauth2/gitlab/" +
          asset.externalEntityProviderId.replace("@", "") +
          "autosetup?redirectTo=" +
          encodeURIComponent(window.location.href);

        return Promise.resolve();
      }

      // create a new one for autosetup
      const accessToken = await onCreateAccessToken({
        description: "DevGuard Autosetup (used inside GitLab Pipeline)",
        scopes: "scan",
        // eslint-disable-next-line react-hooks/purity
        expiryDateUnix: Math.floor(Date.now() / 1000) + 365 * 24 * 60 * 60,
      });

      if (!("privKey" in accessToken) || !accessToken.privKey) {
        toast("Failed to setup GitLab integration: no private key returned");
        return;
      }

      const privKey = accessToken.privKey;

      // set the progress to pending
      setProgress((prev) => {
        const next = { ...prev };
        for (const key in next) {
          next[key] = { ...next[key], status: "pending" };
        }
        return next;
      });

      let url = `/organizations/${activeOrg.slug}/projects/${activeProject?.slug}/assets/${asset?.slug}/integrations/gitlab/autosetup?scanner=${scanner}`;

      if (asset?.externalEntityProviderId) {
        url =
          url +
          `&providerId=${activeOrg.externalEntityProviderId?.replace("@", "")}autosetup`;
      }

      const resp = await apiFetch(url, {
        method: "POST",
        body: JSON.stringify({
          devguardPrivateKey: privKey,
          devguardAssetName: `${activeOrg.slug}/projects/${activeProject?.slug}/assets/${asset?.slug}`,
          devguardApiUrl: devguardApiUrl,
        }),
      });

      if (resp.ok && resp.body) {
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();

        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            let data: Record<string, unknown>;
            try {
              data = JSON.parse(trimmed);
            } catch {
              continue;
            }
            if ("url" in data) {
              window.open(data.url as string, "_blank");
            }
            setProgress((prev) => ({
              ...prev,
              [data.step as string]: {
                status: data.status as "notStarted" | "pending" | "success",
                message: prev[data.step as keyof typeof prev].message,
                url: data.url as string | undefined,
              },
            }));
          }
        }
        return;
      }

      toast("Failed to setup GitLab integration");
    },
  );

  const startPendingAutosetup = useEffectEvent(() => {
    handleAutosetup(true);
  });

  useEffect(() => {
    // check for pending autosetup - if so, we should be able to continue
    if (listenForChanges && readSessionStorage("pending-autosetup")) {
      removeSessionStorage("pending-autosetup");
      startPendingAutosetup();
    }
  }, [listenForChanges]);

  return {
    handleAutosetup,
    progress,
    isLoading,
    Loader,
    pat: accessToken,
    onCreatePat: onCreateAccessToken,
  };
}
